import type { OffersSchemas } from "api-types";
import { useCallback, useMemo, useState } from "react";

import type {
  CollectionFrequency,
  CustomOfferSchema,
  DailySalesCustomOfferParamsSchema,
  FixedCustomOfferParamsSchema,
} from "../schemas";

type CollectionType = "percentage" | "fixed";

type CustomOfferSelection = {
  remittanceRateIndex: number;
  deploymentAmount: number;
  collectionType: CollectionType;
};

export type UseOfferReturn = {
  id: number;
  collectionFrequency: CollectionFrequency;
  deploymentCurrency: string;
  deploymentDate: string;
  percentageOffers: Record<number, DailySalesCustomOfferParamsSchema[]>;
  fixedOffers: Record<number, FixedCustomOfferParamsSchema[]>;
  deploymentAmountInLowestDenomination: number;
  remittanceRateIndex: number;
  remittanceRateDailySalesPercent?: number;
  remittanceRateInLowestDenomination?: number;
  feeAmountInLowestDenomination: number;
  collectionType: CollectionType;
  deploymentAmountLowerBoundInLowestDenomination: number;
  deploymentAmountUpperBoundInLowestDenomination: number;
  isTermLoan: boolean;
  setDeploymentAmountInLowestDenomination: (value: number) => void;
  setRemittanceRateIndex: (value: number) => void;
  setCollectionType: (value: CollectionType) => void;
  currentSelectedValues?: OffersSchemas.SelectedOfferParams | null;
};

type ValidOffers = {
  percentageOffers: Record<number, DailySalesCustomOfferParamsSchema[]>;
  fixedOffers: Record<number, FixedCustomOfferParamsSchema[]>;
  collectionType: CollectionType;
};

const emptyValidOffers: ValidOffers = {
  percentageOffers: {},
  fixedOffers: {},
  collectionType: "percentage",
};

const emptyOfferReturn: UseOfferReturn = {
  id: 0,
  collectionFrequency: "daily",
  deploymentCurrency: "",
  deploymentDate: "",
  percentageOffers: {},
  fixedOffers: {},
  deploymentAmountInLowestDenomination: 0,
  remittanceRateIndex: 0,
  feeAmountInLowestDenomination: 0,
  collectionType: "percentage",
  deploymentAmountLowerBoundInLowestDenomination: 0,
  deploymentAmountUpperBoundInLowestDenomination: 0,
  isTermLoan: false,
  setDeploymentAmountInLowestDenomination: () => {},
  setRemittanceRateIndex: () => {},
  setCollectionType: () => {},
};

/**
 * Retrieves the cheapest percentage offers from the given array of offers.
 * The offers are sorted based on the remittance rate daily sales percentage and then fee amount in ascending order.
 * If multiple offers have the same remittance rate daily sales percentage, only the one with the lowest fee will be included in the result.
 *
 * @param offers - The array of offers to filter.
 * @returns An array of the cheapest percentage offers.
 */
export function getCheapestPercentageOffers(
  offers: DailySalesCustomOfferParamsSchema[]
): Array<DailySalesCustomOfferParamsSchema> {
  const sortedOffers = offers.sort((a, b) => {
    if (a.remittanceRateDailySalesPercent === b.remittanceRateDailySalesPercent) {
      return a.feeAmountInLowestDenomination - b.feeAmountInLowestDenomination;
    }
    return a.remittanceRateDailySalesPercent - b.remittanceRateDailySalesPercent;
  });

  const optimalOffers = sortedOffers.reduce((cheapestOffers, curr) => {
    if (cheapestOffers.length === 0) {
      return [curr];
    }

    const lastOffer = cheapestOffers[cheapestOffers.length - 1];
    if (curr.remittanceRateDailySalesPercent === lastOffer.remittanceRateDailySalesPercent) {
      if (curr.feeAmountInLowestDenomination < lastOffer.feeAmountInLowestDenomination) {
        return [...cheapestOffers.slice(0, cheapestOffers.length - 1), curr]; // replace the last offer with the current one
      }
    } else {
      return [...cheapestOffers, curr];
    }

    return cheapestOffers;
  }, [] as Array<DailySalesCustomOfferParamsSchema>);

  return optimalOffers;
}

/**
 * Retrieves the cheapest fixed offers from the given array of offers.
 * The offers are sorted based on the fixed remittance amount and then fee amount in ascending order.
 * If multiple offers have the same fixed remittance amount, only the one with the lowest fee will be included in the result.
 * @param offers - An array of FixedCustomOfferParamsSchema representing the fixed offers.
 * @returns An array of FixedCustomOfferParamsSchema representing the optimal fixed offers.
 */
export function getCheapestFixedOffers(offers: FixedCustomOfferParamsSchema[]): Array<FixedCustomOfferParamsSchema> {
  const sortedOffers = offers.sort((a, b) => {
    if (a.remittanceRateInLowestDenomination === b.remittanceRateInLowestDenomination) {
      return a.feeAmountInLowestDenomination - b.feeAmountInLowestDenomination;
    }
    return a.remittanceRateInLowestDenomination - b.remittanceRateInLowestDenomination;
  });

  const optimalOffers = sortedOffers.reduce((cheapestOffers, curr) => {
    if (cheapestOffers.length === 0) {
      return [curr];
    }

    const lastOffer = cheapestOffers[cheapestOffers.length - 1];
    if (curr.remittanceRateInLowestDenomination === lastOffer.remittanceRateInLowestDenomination) {
      if (curr.feeAmountInLowestDenomination < lastOffer.feeAmountInLowestDenomination) {
        return [...cheapestOffers.slice(0, cheapestOffers.length - 1), curr]; // replace the last offer with the current one
      }
    } else {
      return [...cheapestOffers, curr];
    }

    return cheapestOffers;
  }, [] as Array<FixedCustomOfferParamsSchema>);

  return optimalOffers;
}

/**
 * Custom hook that calculates and returns valid offers based on the provided custom offers.
 * @param customOffers The custom offers object.
 * @returns An object containing the calculated valid offers and utility functions.
 */
export const useValidOffers = (
  customOffer: CustomOfferSchema | null | undefined,
  collectionType?: CollectionType
): UseOfferReturn => {
  // Calculate the lower and upper bounds of the deployment amount based on the provided offers
  const offerAmounts = customOffer?.validOffersByAmount
    .map((offer) => offer.deploymentAmountInLowestDenomination)
    .sort((a, b) => a - b);
  const deploymentAmountLowerBoundInLowestDenomination = offerAmounts?.[0] ?? 0;
  const deploymentAmountUpperBoundInLowestDenomination = offerAmounts?.[offerAmounts.length - 1] ?? 0;

  // Utility function to get the closest deployment amount based on a given value
  const getClosestDeploymentAmountInLowestDenomination = useCallback(
    (value: number): number =>
      customOffer?.validOffersByAmount.reduce(
        (prev, { deploymentAmountInLowestDenomination: curr }) =>
          Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
        0
      ) ?? 0,
    [customOffer]
  );

  /* Dedupe the offers provided by the backend, sort them, and format them for display.
   * This logic will likely be complete by the backend in future.
   */
  const offersByAmount = useMemo<ValidOffers>(() => {
    if (!customOffer) {
      return emptyValidOffers;
    }
    return customOffer.validOffersByAmount.reduce((offersLookup, offer) => {
      // Store the percentage remittance rates for the current deployment amount
      offersLookup.percentageOffers[offer.deploymentAmountInLowestDenomination] = getCheapestPercentageOffers(
        offer.dailySalesOffers
      );
      // Store the fixed remittance amounts for the current deployment amount
      offersLookup.fixedOffers[offer.deploymentAmountInLowestDenomination] = getCheapestFixedOffers(offer.fixedOffers);

      if (collectionType) {
        offersLookup.collectionType = collectionType;
      }
      // If there are any fixed offers available for any deployment amount, set the collection type to fixed
      else if (offersLookup.fixedOffers[offer.deploymentAmountInLowestDenomination].length > 0) {
        offersLookup.collectionType = "fixed";
      }

      return offersLookup;
    }, emptyValidOffers);
  }, [customOffer, collectionType]);

  const findRemittanceRateIndex = (
    deploymentAmount: number,
    dailyCollectionPercentage?: number | null,
    fixedCollectionAmountInLowestDenomination?: number | null
  ) => {
    if (dailyCollectionPercentage) {
      const offers = offersByAmount.percentageOffers[deploymentAmount];

      return offers.findIndex(
        (offer) => Number(offer.remittanceRateDailySalesPercent) === Number(dailyCollectionPercentage)
      );
    }
    if (fixedCollectionAmountInLowestDenomination) {
      const offers = offersByAmount.fixedOffers[deploymentAmount];
      return offers.findIndex(
        (offer) => offer.remittanceRateInLowestDenomination === fixedCollectionAmountInLowestDenomination
      );
    }
    return 0;
  };

  const calculateInitialState = () => {
    if (customOffer?.selectedParams) {
      return {
        deploymentAmount: customOffer?.selectedParams?.deploymentAmountInLowestDenomination,
        remittanceRateIndex: findRemittanceRateIndex(
          customOffer.selectedParams.deploymentAmountInLowestDenomination,
          customOffer.selectedParams.dailyCollectionPercentage,
          customOffer.selectedParams.fixedCollectionAmountInLowestDenomination
        ),
        collectionType: offersByAmount.collectionType,
      };
    }
    return {
      deploymentAmount: getClosestDeploymentAmountInLowestDenomination(
        deploymentAmountUpperBoundInLowestDenomination -
          (deploymentAmountUpperBoundInLowestDenomination - deploymentAmountLowerBoundInLowestDenomination) / 2
      ),
      remittanceRateIndex: 0,
      collectionType: offersByAmount.collectionType,
    };
  };

  const [customOfferSelection, setCustomOfferSelection] = useState<CustomOfferSelection>(() => calculateInitialState());

  const setRemittanceRateIndex = (index: number) => {
    setCustomOfferSelection({
      deploymentAmount: customOfferSelection.deploymentAmount,
      remittanceRateIndex: index,
      collectionType: customOfferSelection.collectionType,
    });
  };

  const setCollectionType = (collectionType: CollectionType) => {
    setCustomOfferSelection({
      deploymentAmount: customOfferSelection.deploymentAmount,
      remittanceRateIndex: 0,
      collectionType: collectionType,
    });
  };

  const setDeploymentAmountInLowestDenomination = (value: number) => {
    const amount = getClosestDeploymentAmountInLowestDenomination(value);
    if (customOfferSelection.deploymentAmount !== amount) {
      setCustomOfferSelection({
        deploymentAmount: amount,
        remittanceRateIndex: 0,
        collectionType: customOfferSelection.collectionType,
      });
    }
  };

  if (!customOffer) {
    return emptyOfferReturn;
  }

  // Return the best valid offers, current offer state and utility functions
  return {
    ...offersByAmount,
    id: customOffer.id,
    collectionFrequency: customOffer.collectionFrequency,
    deploymentCurrency: customOffer.currency,
    deploymentDate: customOffer.deploymentDate,
    deploymentAmountInLowestDenomination: customOfferSelection.deploymentAmount,
    remittanceRateIndex: customOfferSelection.remittanceRateIndex,
    remittanceRateDailySalesPercent:
      customOfferSelection.collectionType === "percentage"
        ? offersByAmount.percentageOffers[customOfferSelection.deploymentAmount][
            customOfferSelection.remittanceRateIndex
          ]?.remittanceRateDailySalesPercent
        : undefined,
    remittanceRateInLowestDenomination:
      customOfferSelection.collectionType === "fixed"
        ? offersByAmount.fixedOffers[customOfferSelection.deploymentAmount][customOfferSelection.remittanceRateIndex]
            ?.remittanceRateInLowestDenomination
        : undefined,
    collectionType: customOfferSelection.collectionType,
    deploymentAmountLowerBoundInLowestDenomination,
    deploymentAmountUpperBoundInLowestDenomination,
    feeAmountInLowestDenomination:
      customOfferSelection.collectionType === "percentage"
        ? offersByAmount.percentageOffers[customOfferSelection.deploymentAmount][
            customOfferSelection.remittanceRateIndex
          ].feeAmountInLowestDenomination
        : offersByAmount.fixedOffers[customOfferSelection.deploymentAmount][customOfferSelection.remittanceRateIndex]
            .feeAmountInLowestDenomination,
    isTermLoan: customOffer.isEligibleForTermLoan && customOfferSelection.collectionType === "fixed",
    setDeploymentAmountInLowestDenomination,
    setRemittanceRateIndex,
    setCollectionType,
  };
};
